import React, {useEffect, useRef} from 'react';

// Radio del organismo en función de cuánto sabe: arranca en 86px y crece medio píxel por hecho,
// con techo en 120. Que crezca es la mitad del punto — el orbe TIENE que verse distinto en un
// cliente con 4 hechos y en uno con 200, o es un adorno.
const BASE_RADIUS = 86;
const RADIUS_PER_FACT = 0.5;
const MAX_RADIUS = 120;

// Cuántas partículas entran por cada evento de absorción, y de dónde salen.
const PARTICLES_PER_ABSORB = 14;
const SPAWN_RADIUS_FACTOR = 2.2;

// La RESPIRACIÓN. El radio oscila `±BREATH_AMPLITUDE` px con un período de `2π · BREATH_PERIOD_MS`,
// y las dos cosas se aceleran mientras el orbe está excitado (absorbiendo material).
//
// ⚠️ Estos números NO salen del mockup: salen de apartarse de él a propósito. Medido el 08/09, la
// respiración de `dashbo-ia-booster-con-barra-lateral_9.html` es IDÉNTICA a la que había acá —
// `amp = 5 + excite*6`, `spd = 1400 - excite*380`, byte por byte, y con el `sabeCount = 45` del
// mockup el radio base también da igual (108,5 px en los dos). O sea que no había nada que
// «acercar»: el mockup respira exactamente igual de poco.
//
// Lo que se movió, entonces, es el pedido de Pablo («no me respira… muy poquitito»), y se movió por
// los dos lados porque el que más pesa es el PERÍODO: a 8,8 s por ciclo, en los 2 s que alguien mira
// el orbe el radio recorre ~3,5 px de 110 y la respiración es invisible. A 6,3 s y ±9 px recorre
// ~9 px en la misma mirada. El techo lo pone la caja del <canvas>: el anillo visible más externo
// queda en R+17 ≈ 147 px contra los 190 px de media altura, así que hay margen de sobra.
const BREATH_AMPLITUDE = 9;
const BREATH_AMPLITUDE_EXCITED = 6;
const BREATH_PERIOD_MS = 1000;
const BREATH_PERIOD_EXCITED_MS = 280;

// El anillo exterior late con su PROPIO período, y tiene que ser primo respecto del de la
// respiración: es el desfasaje entre los dos lo que hace que el orbe se lea orgánico y no como una
// sola cosa que pulsa. Antes eran 1400 y 1100; al bajar la respiración a 1000 quedaron casi en fase
// (review R-05e), así que el anillo baja en la misma proporción.
const OUTER_RING_PERIOD_MS = 790;

/**
 * Cuánto se aparta el radio de su base en este instante. Función PURA para poder afirmar por test
 * lo único que no se puede mirar en pantalla: que con `prefers-reduced-motion` el orbe no respira.
 */
export function orbBreath(ts, excite = 0, reduceMotion = false) {
    if (reduceMotion) return 0;
    const amplitude = BREATH_AMPLITUDE + excite * BREATH_AMPLITUDE_EXCITED;
    const period = BREATH_PERIOD_MS - excite * BREATH_PERIOD_EXCITED_MS;
    return Math.sin(ts / period) * amplitude;
}

function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * El orbe de procesamiento: el organismo de la memoria del cliente.
 *
 * Es la pieza que Pau destacó por nombre en la sesión del 21/08 ("espectacular y brutal"), y el
 * motivo por el que se eligió esta iteración. Lo que comunica, en orden de importancia:
 *
 * 1. **La memoria de este cliente existe y tiene tamaño** — el radio sale de `count`.
 * 2. **Está viva** — respira con un seno lento, aunque no pase nada.
 * 3. **Absorbe** — cuando entra material, las partículas caen en espiral hacia el centro y el
 *    orbe late al tragarlas. Es la respuesta visual a "subí algo", sin un spinner.
 *
 * Portado del `<canvas>` del mockup `dashbo-ia-booster-con-barra-lateral_9.html`, con tres cosas
 * que el mockup no necesitaba y un componente montado en una SPA sí: cancelar el rAF al
 * desmontar, re-dimensionar el bitmap cuando cambia la caja, y respetar `prefers-reduced-motion`
 * (ahí se dibuja UN frame quieto en vez de no dibujar nada — sin eso el orbe desaparece).
 *
 * `absorbToken` es un contador: cada vez que sube, se sueltan partículas. Se pasa así y no como
 * callback imperativo para que el padre no tenga que sostener un ref a este componente.
 */
export function BoosterOrb({count = 0, absorbToken = 0, className = ''}) {
    const canvasRef = useRef(null);
    // `kick` lo publica el efecto de dibujo: es cómo la absorción vuelve a arrancar el loop cuando
    // se había frenado. `running` evita agendar dos rAF en paralelo.
    const stateRef = useRef({particles: [], pulse: 0, excite: 0, count: 0, running: false, kick: null});

    // El conteo se lee desde el loop de dibujo, que no re-arranca en cada render: va por ref.
    stateRef.current.count = count;

    // ...pero con movimiento reducido el loop dibuja UN frame y se frena, y el conteo llega por API
    // después del montaje. Sin este empujón el orbe se quedaba con el radio de 0 hechos (86 px)
    // mientras el número de al lado decía 68 — dos afirmaciones sobre lo mismo, y una falsa.
    useEffect(() => {
        const {kick} = stateRef.current;
        if (typeof kick === 'function') kick();
    }, [count]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;

        const state = stateRef.current;
        const reduceMotion = prefersReducedMotion();
        let frame = null;
        let alive = true;

        const ratio = () => (window.devicePixelRatio || 1);

        function resize() {
            const rect = canvas.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            canvas.width = Math.round(rect.width * ratio());
            canvas.height = Math.round(rect.height * ratio());
            ctx.setTransform(ratio(), 0, 0, ratio(), 0, 0);
            // 🔴 Asignar `canvas.width` BORRA el bitmap. Con movimiento reducido el loop dibuja un
            // frame y se frena, así que el primer reflow posterior —y el `ResizeObserver` dispara
            // uno al observar— dejaba el orbe transparente PARA SIEMPRE: el conteo y las etiquetas
            // flotando sobre el fondo, sin círculo ni halo. Lo encontró el QA del 09/09, y es
            // exactamente lo que el comentario de arriba promete que no pasa.
            // Sin reduced-motion `start()` no hace nada (el loop nunca se frenó).
            start();
        }

        function draw(ts) {
            if (!alive) return;
            const rect = canvas.getBoundingClientRect();
            const w = rect.width;
            const h = rect.height;
            if (!w || !h) {
                frame = window.requestAnimationFrame(draw);
                return;
            }
            const cx = w / 2;
            const cy = h / 2;
            ctx.clearRect(0, 0, w, h);

            // El radio no puede pasarse de la caja: sin este segundo tope, un cliente con muchos
            // hechos dibuja un orbe recortado por los bordes del <canvas>.
            let base = Math.min(BASE_RADIUS + state.count * RADIUS_PER_FACT, MAX_RADIUS);
            base = Math.min(base, Math.min(w, h) / 2 / 1.6 - 8);
            if (base <= 0) {
                frame = window.requestAnimationFrame(draw);
                return;
            }

            const R = base + orbBreath(ts, state.excite, reduceMotion) + state.pulse;

            if (state.pulse > 0) state.pulse = Math.max(0, state.pulse - 0.55);
            if (state.excite > 0) state.excite = Math.max(0, state.excite - 0.009);

            const glow = ctx.createRadialGradient(cx, cy, R * 0.1, cx, cy, R * 1.6);
            glow.addColorStop(0, 'rgba(185,163,245,.55)');
            glow.addColorStop(0.35, 'rgba(138,92,246,.30)');
            glow.addColorStop(0.7, 'rgba(110,77,224,.12)');
            glow.addColorStop(1, 'rgba(110,77,224,0)');
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(cx, cy, R * 1.6, 0, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = 'rgba(185,163,245,.5)';
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, Math.PI * 2);
            ctx.stroke();

            ctx.strokeStyle = 'rgba(185,163,245,.14)';
            ctx.beginPath();
            ctx.arc(cx, cy, R + 14 + (reduceMotion ? 0 : Math.sin(ts / OUTER_RING_PERIOD_MS) * 3),
                0, Math.PI * 2);
            ctx.stroke();

            for (let i = state.particles.length - 1; i >= 0; i--) {
                const p = state.particles[i];
                if (p.delay > 0) { p.delay -= 1; continue; }
                p.rad -= p.rv;
                p.angle += p.av;
                p.rad += Math.sin(ts / 170 + p.wobble) * 0.55;
                const px = cx + Math.cos(p.angle) * p.rad;
                const py = cy + Math.sin(p.angle) * p.rad;
                const alpha = Math.min(1, Math.max(0, (base * 2.4 - p.rad) / (base * 1.6))) * 0.9;
                ctx.fillStyle = `rgba(185,163,245,${alpha.toFixed(2)})`;
                ctx.beginPath();
                ctx.arc(px, py, p.r, 0, Math.PI * 2);
                ctx.fill();
                // La estela: el mismo punto, más grande y casi transparente, un paso atrás en la
                // espiral. Es lo que hace que se lea como movimiento y no como puntos parpadeando.
                ctx.fillStyle = `rgba(185,163,245,${(alpha * 0.28).toFixed(2)})`;
                ctx.beginPath();
                ctx.arc(px - Math.cos(p.angle - p.av * 6) * 3, py - Math.sin(p.angle - p.av * 6) * 3,
                    p.r * 1.7, 0, Math.PI * 2);
                ctx.fill();
                if (p.rad <= R * 0.45) {
                    state.particles.splice(i, 1);
                    state.pulse = Math.min(state.pulse + 1.6, 11);
                    state.excite = Math.min(state.excite + 0.09, 0.7);
                }
            }

            // Con reduced-motion no se anima de fondo, pero sí mientras haya partículas cayendo:
            // esa animación es la RESPUESTA a una acción del usuario, no decoración ambiente.
            if (!reduceMotion || state.particles.length) {
                frame = window.requestAnimationFrame(draw);
            } else {
                state.running = false;
            }
        }

        function start() {
            if (!alive || state.running) return;
            state.running = true;
            frame = window.requestAnimationFrame(draw);
        }

        state.kick = start;
        resize();
        start();

        let observer = null;
        if (typeof window.ResizeObserver === 'function') {
            observer = new window.ResizeObserver(() => resize());
            observer.observe(canvas);
        }
        window.addEventListener('resize', resize);

        return () => {
            alive = false;
            state.running = false;
            state.kick = null;
            if (frame !== null) window.cancelAnimationFrame(frame);
            if (observer) observer.disconnect();
            window.removeEventListener('resize', resize);
        };
    }, []);

    // Absorción: sólo cuando el token SUBE (el primer render no cuenta — sería una lluvia de
    // partículas cada vez que se entra a la pantalla).
    const lastTokenRef = useRef(absorbToken);
    useEffect(() => {
        if (absorbToken === lastTokenRef.current) return;
        lastTokenRef.current = absorbToken;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const spawn = Math.min(rect.width, rect.height) / 2 / 1.6 * SPAWN_RADIUS_FACTOR;
        const state = stateRef.current;
        for (let i = 0; i < PARTICLES_PER_ABSORB; i++) {
            state.particles.push({
                angle: Math.random() * Math.PI * 2,
                rad: spawn * (0.85 + Math.random() * 0.3),
                rv: 0.9 + Math.random() * 0.7,
                av: (Math.random() > 0.5 ? 1 : -1) * (0.008 + Math.random() * 0.012),
                r: 1.4 + Math.random() * 1.8,
                wobble: Math.random() * Math.PI * 2,
                delay: Math.round(Math.random() * 26),
            });
        }
        // Con `prefers-reduced-motion` el loop se frena cuando no queda nada que mover, así que
        // hay que volver a arrancarlo a mano. Sin marca de motion el loop nunca se detuvo y `kick`
        // no hace nada (chequea `running`).
        if (typeof state.kick === 'function') state.kick();
    }, [absorbToken]);

    return <canvas ref={canvasRef} className={`iab-orb-canvas ${className}`} aria-hidden="true"/>;
}

export default BoosterOrb;
